// The energy page — everything a current sensor (DeviceID 16 three-phase,
// 82 single-phase) can tell you, on one page, for BOTH shells.
//
//   Tariff formula (editable; the page recalculates as you type)
//   Live · Bill period (so far / projected, line by line)
//   What the readings say (plain-language findings)
//   Units per day · Average load by hour
//   Waste · Phase balance · Voltage · Power factor · Peak demand · Data quality
//   One day in detail · History and bill check
//
// Data: core/devices/energyData (hourly buckets, cached), maths:
// core/devices/energyAnalysis + energyTariff, settings: energySettings.
// The phone wraps this in DeviceAppBar, the desktop in DeviceShell.

import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  V_HIGH, addDays, analyse, periodMonths, periodOf, projectPeriod, ymd,
} from '../../../core/devices/energyAnalysis';
import { loadDays, loadLatest } from '../../../core/devices/energyData';
import { loadEnergySettings, normalizeSettings, saveEnergySettings } from '../../../core/devices/energySettings';
import { billFor } from '../../../core/devices/energyTariff';
import { parseServerTime } from '../../../core/devices/firmwareInfo';
import TariffEditor from './TariffEditor';
import {
  BalanceCard, BillCard, DailyChart, Findings, LiveStrip, PeakCard, PowerFactorCard, ProfileChart,
  QualityCard, SourceNote, VoltageCard, WasteCard,
} from './EnergySections';
import { BillHistory, DayExplorer } from './EnergyHistory';
import ApplianceCard, { ApplianceDialog } from './ApplianceCard';
import {
  PHASE, WEEKDAYS, dateRange, kwh, rupees, watts,
} from './energyFormat';

const LIVE_MS = 30000;

/** Plain-language findings — only what the numbers clearly support. */
function findingsOf(report, settings, projectedBill, lastBill) {
  const out = [];
  const work = report.daily.filter((x) => x.type === 'work' && x.expectedS >= 86400 && x.missingS < 3 * 3600);
  const off = report.daily.filter((x) => x.type === 'off' && x.expectedS >= 86400 && x.missingS < 3 * 3600);
  const avg = (xs) => xs.reduce((a, x) => a + x.total, 0) / (xs.length || 1);
  if (work.length) out.push(`A normal work day uses about ${kwh(avg(work), 0)}${off.length ? `, an off day (${settings.offDays.map((d) => WEEKDAYS[d]).join(', ')}) about ${kwh(avg(off), 1)}` : ''}.`);
  if (report.opensAt != null) out.push(`The load starts around ${String(report.opensAt).padStart(2, '0')}:00 and settles back to the night level by ${String(report.closesAt % 24).padStart(2, '0')}:00.`);
  if (report.baseW > 20) out.push(`About ${watts(report.baseW)} runs all night, every night — ${rupees(report.waste.baseRupeesYear)} a year. Worth finding what it is (router, CCTV, fridge, a light left on).`);
  if (report.phases.length > 1) {
    const top = report.phases.reduce((a, p) => (report.byPhase[p] > report.byPhase[a] ? p : a), report.phases[0]);
    const total = report.phases.reduce((a, p) => a + report.byPhase[p], 0) || 1;
    out.push(`${PHASE[top].name} carries ${Math.round((report.byPhase[top] / total) * 100)}% of all units${report.imbalancePct >= 30 ? ' — the phases are unbalanced' : ''}.`);
  }
  report.phases.forEach((p) => {
    const v = report.voltage[p];
    if (v.hoursHigh >= 3) out.push(`${report.phases.length > 1 ? PHASE[p].name : 'The supply'} averaged above ${V_HIGH} V for ${v.hoursHigh} hours (highest ${v.max.toFixed(0)} V) — over-voltage.`);
  });
  const lowPf = report.phases.filter((p) => report.powerFactor[p] != null && report.powerFactor[p] < 0.85);
  if (lowPf.length) out.push(`Power factor is low on ${lowPf.map((p) => PHASE[p].label).join(', ')} — mostly motor / compressor load.`);
  if (report.peak && settings.sanctionedKw && report.peak.w / 1000 > settings.sanctionedKw * 0.9) {
    out.push(`The peak (${watts(report.peak.w)}) is close to the sanctioned ${settings.sanctionedKw} kW.`);
  }
  if (report.coveragePct < 97) out.push(`The sensor missed ${(100 - report.coveragePct).toFixed(1)}% of the time; ${kwh(report.estimatedKwh, 0)} of the units are estimated.`);
  if (lastBill && projectedBill) {
    const d = ((projectedBill.total - (lastBill.amount || 0)) / (lastBill.amount || 1)) * 100;
    if (lastBill.amount) {
      out.push(`This period is heading for ${rupees(projectedBill.total)}, ${Math.abs(d).toFixed(0)}% ${d >= 0 ? 'above' : 'below'} the last bill you entered (${rupees(lastBill.amount)}${lastBill.estimated ? ', an average bill — the meter was not read' : ''}).`);
    }
  }
  return out;
}

export default function EnergyPanel({ device, phaseCount = 3 }) {
  const uno = String(device.UniqueNumber);
  const phases = useMemo(() => (phaseCount === 3 ? [1, 2, 3] : [1]), [phaseCount]);

  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [stored, setStored] = useState('default');
  const [saving, setSaving] = useState(false);
  const [saveNote, setSaveNote] = useState(null);

  const [days, setDays] = useState(null);
  const [source, setSource] = useState('');
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [live, setLive] = useState(null);

  // What the analysis cards describe: the last 30 days, or a period picked in history.
  const [view, setView] = useState({ kind: 'last30' });
  const [viewDays, setViewDays] = useState(null);
  // Appliance dialog: null = closed, -1 = new, i = editing appliances[i].
  const [editing, setEditing] = useState(null);

  const now = useMemo(() => new Date(), [days]); // eslint-disable-line react-hooks/exhaustive-deps
  const today = useMemo(() => new Date(now.getFullYear(), now.getMonth(), now.getDate()), [now]);
  const tomorrow = addDays(today, 1);
  const windowFrom = addDays(today, -31);

  // ── Settings ─────────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    loadEnergySettings(device).then((r) => {
      if (!alive) return;
      setSaved(r.settings);
      setDraft(r.settings);
      setStored(r.stored);
    });
    return () => { alive = false; };
  }, [uno]); // eslint-disable-line react-hooks/exhaustive-deps

  // `keepKey` ('bills' | 'appliances'): saving one of those while the tariff
  // has unsaved edits saves only that key — the edits stay on screen, unsaved.
  const persist = useCallback(async (next, keepKey) => {
    setSaving(true);
    const clean = normalizeSettings(next);
    const res = await saveEnergySettings(uno, clean);
    setSaving(false);
    setSaved(clean);
    setDraft((d) => (keepKey && d ? { ...d, [keepKey]: clean[keepKey] } : clean));
    setStored(res.ok ? 'server' : 'local');
    setSaveNote(res.ok ? { ok: true, text: 'Saved — everyone with this sensor sees the same tariff.' } : { ok: false, text: res.message });
  }, [uno]);

  const dirty = saved && draft && JSON.stringify(normalizeSettings(draft)) !== JSON.stringify(saved);

  // ── Readings: the last 31 days (covers any bill period up to today) ──────
  const loadWindow = useCallback(async () => {
    setError('');
    try {
      const r = await loadDays(uno, windowFrom, tomorrow, {
        onProgress: (d, t) => setProgress(t ? `${d} of ${t} days` : ''),
      });
      setDays(r.days);
      setSource(r.source);
    } catch (e) {
      setError('Could not load the readings. Check the connection and pull to refresh.');
      setDays((x) => x || []);
    } finally {
      setProgress('');
    }
  }, [uno]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadWindow(); }, [loadWindow]);

  // Today keeps growing: re-read it every 5 minutes (finished days are cached).
  useEffect(() => {
    const id = setInterval(() => { loadWindow(); }, 5 * 60000);
    return () => clearInterval(id);
  }, [loadWindow]);

  // ── Live strip ───────────────────────────────────────────────────────────
  const liveRef = useRef(true);
  useEffect(() => {
    liveRef.current = true;
    const tick = () => loadLatest(uno).then((x) => { if (liveRef.current) setLive(x); }).catch(() => {});
    tick();
    const id = setInterval(tick, LIVE_MS);
    return () => { liveRef.current = false; clearInterval(id); };
  }, [uno]);

  // ── A period picked in the history table ─────────────────────────────────
  useEffect(() => {
    if (view.kind !== 'period') { setViewDays(null); return undefined; }
    let alive = true;
    setViewDays(null);
    loadDays(uno, view.period.start, view.period.end)
      .then((r) => { if (alive) setViewDays(r.days); })
      .catch(() => { if (alive) setViewDays([]); });
    return () => { alive = false; };
  }, [uno, view]);

  // ── The maths ────────────────────────────────────────────────────────────
  const calc = useMemo(() => {
    if (!draft || !days) return null;
    const settings = normalizeSettings(draft);
    const firstData = (() => {
      const withData = days.filter((d) => d.hours.length).map((d) => d.d).sort();
      // Leading empty days in the window are "before the sensor", not a gap.
      return withData.length && withData[0] > ymd(windowFrom) ? withData[0] : null;
    })();
    const opts = (from, to) => ({ from, to, now, firstData, phases });

    const period = periodOf(now, settings.cycleDay);
    const inPeriod = days.filter((d) => d.d >= ymd(period.start));
    const rPeriod = analyse(inPeriod, settings, opts(period.start, tomorrow));
    const r30 = analyse(days, settings, opts(addDays(today, -30), tomorrow));
    const projected = projectPeriod(rPeriod, period, r30.daily, settings.offDays, now);
    const months = periodMonths(period);
    const bill = billFor(rPeriod.recordedKwh + rPeriod.estimatedKwh, settings.tariff, { sanctionedKw: settings.sanctionedKw, months });
    const projectedBill = billFor(projected.total, settings.tariff, { sanctionedKw: settings.sanctionedKw, months });

    let report = r30;
    let viewFrom = addDays(today, -30);
    let viewTo = tomorrow;
    let viewLabel = `Last 30 days · ${dateRange(addDays(today, -30), tomorrow)}`;
    if (view.kind === 'period' && viewDays) {
      report = analyse(viewDays, settings, { ...opts(view.period.start, view.period.end), firstData: null });
      viewLabel = `Bill period · ${dateRange(view.period.start, view.period.end)}`;
      viewFrom = view.period.start;
      viewTo = view.period.end;
    }
    // Days with readings missing for more than half the day — an appliance
    // card must not call those "not used".
    const offline = new Set(report.daily.filter((x) => x.missingS > 12 * 3600).map((x) => x.d));
    const lastBill = settings.bills[0] || null;
    return {
      settings, period, rPeriod, projected, bill, projectedBill, report, viewLabel, viewFrom, viewTo, offline,
      findings: findingsOf(report, settings, projectedBill, lastBill),
    };
  }, [draft, days, view, viewDays, now]); // eslint-disable-line react-hooks/exhaustive-deps

  const lastSeen = parseServerTime(device.LastUpdatedDateTime ?? device.lastUpdatedDateTime);

  if (!draft || !days) {
    return (
      <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">{progress ? `Loading readings — ${progress}` : 'Loading readings…'}</Typography>
      </Box>
    );
  }

  const s = calc.settings;
  const r = calc.report;
  const twoCol = { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: { xs: 1.5, sm: 2 }, maxWidth: 1200, mx: 'auto', width: '100%', boxSizing: 'border-box' }}>
      <TariffEditor
        settings={draft}
        onChange={(x) => { setDraft(x); setSaveNote(null); }}
        dirty={dirty}
        saving={saving}
        saveNote={saveNote || (stored === 'local' ? { ok: false, text: 'These settings are saved on this device only — the server column is not there yet.' } : null)}
        onSave={() => persist(draft)}
        onReset={() => { setDraft(saved); setSaveNote(null); }}
      />

      {error && <Alert severity="warning">{error}</Alert>}

      <LiveStrip live={live} phases={phases} lastSeen={lastSeen} />

      <BillCard
        period={calc.period}
        bill={calc.bill}
        projected={calc.projected}
        projectedBill={calc.projectedBill}
        report={calc.rPeriod}
        settings={s}
      />

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
        <Typography variant="overline" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>{calc.viewLabel}</Typography>
        {view.kind === 'period' && <Chip size="small" label="Back to last 30 days" onClick={() => setView({ kind: 'last30' })} />}
        <SourceNote source={source} />
        {view.kind === 'period' && !viewDays && <CircularProgress size={16} />}
      </Box>

      <Findings items={calc.findings} />

      {s.appliances.map((a, i) => (
        <ApplianceCard
          key={`${a.phase}-${a.minW}-${a.name}`}
          uno={uno}
          appliance={a}
          settings={s}
          from={calc.viewFrom}
          to={calc.viewTo}
          totalKwh={r.recordedKwh}
          offline={calc.offline}
          onEdit={() => setEditing(i)}
        />
      ))}
      {s.appliances.length < 3 && (
        <Box>
          <Button size="small" startIcon={<AddIcon />} onClick={() => setEditing(-1)}>
            Track an appliance on its own phase
          </Button>
        </Box>
      )}
      <ApplianceDialog
        open={editing != null}
        phases={phases}
        value={editing == null ? null : (editing === -1
          ? { name: 'Office AC', phase: phases[phases.length - 1], minW: 500 }
          : s.appliances[editing])}
        onClose={() => setEditing(null)}
        onRemove={editing != null && editing >= 0
          ? () => { persist({ ...saved, appliances: saved.appliances.filter((_, j) => j !== editing) }, 'appliances'); setEditing(null); }
          : null}
        onSave={(a) => {
          const list = [...saved.appliances];
          if (editing === -1) list.push(a); else list[editing] = a;
          persist({ ...saved, appliances: list }, 'appliances');
          setEditing(null);
        }}
      />
      <DailyChart report={r} />
      <ProfileChart report={r} settings={s} />

      <Box sx={twoCol}>
        <WasteCard report={r} settings={s} />
        <PeakCard report={r} settings={s} />
        <BalanceCard report={r} />
        <PowerFactorCard report={r} />
        <VoltageCard report={r} />
        <QualityCard report={r} />
      </Box>

      <DayExplorer uno={uno} phases={phases} />

      <BillHistory
        uno={uno}
        phases={phases}
        settings={s}
        onSaveBills={(bills) => persist({ ...saved, bills }, 'bills')}
        onPickPeriod={(p) => setView({ kind: 'period', period: p })}
      />
    </Box>
  );
}
